import * as React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown, ChevronUp, PanelLeft } from 'lucide-react';

import { Kbd } from 'components/atoms/Kbd';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { cn } from 'utils/cn';
import { Input } from './Input';
import { Separator } from './Separator';
import { Sheet, SheetContent } from './Sheet';
import { Skeleton } from './Skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';

const SIDEBAR_COOKIE_NAME = 'sidebar:state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days
const SIDEBAR_WIDTH_PX = 255;
const SIDEBAR_WIDTH_ICON_PX = 60;

const SIDEBAR_WIDTH = `${SIDEBAR_WIDTH_PX}px`;
const SIDEBAR_WIDTH_MOBILE = '288px';
const SIDEBAR_WIDTH_ICON = `${SIDEBAR_WIDTH_ICON_PX}px`;
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

export type SidebarContextValue = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  width: number;
  toggleSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextValue | null>(null);

const SIDEBAR_DEFAULTS: SidebarContextValue = {
  state: 'collapsed',
  open: false,
  setOpen: () => {},
  openMobile: false,
  setOpenMobile: () => {},
  isMobile: false,
  width: SIDEBAR_WIDTH_ICON_PX,
  toggleSidebar: () => {},
};

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  return context ?? SIDEBAR_DEFAULTS;
}

/** Returns the sidebar context, or `null` when rendered outside a SidebarProvider. */
export function useSidebarOptional(): SidebarContextValue | null {
  return useContext(SidebarContext);
}

export const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const isMobile = !useBreakpoint('sm');
    const [openMobile, setOpenMobile] = useState(false);

    const [_open, _setOpen] = useState(() => {
      if (typeof document !== 'undefined') {
        const cookie = document.cookie
          .split('; ')
          .find(row => row.startsWith(`${SIDEBAR_COOKIE_NAME}=`));
        if (cookie) {
          return cookie.split('=')[1] === 'true';
        }
      }
      return defaultOpen;
    });
    const open = openProp ?? _open;
    const setOpen = useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }

        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenProp, open],
    );

    const toggleSidebar = useCallback(() => {
      setOpen(prev => !prev);
    }, [setOpen]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    useEffect(() => {
      if (!isMobile) {
        setOpenMobile(false);
      }
    }, [isMobile]);

    const state = open ? 'expanded' : 'collapsed';
    const width = isMobile
      ? 0
      : open
        ? SIDEBAR_WIDTH_PX
        : SIDEBAR_WIDTH_ICON_PX;

    const contextValue = useMemo<SidebarContextValue>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        width,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, width, openMobile, toggleSidebar],
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            ref={ref}
            className={cn(
              'group/sidebar-wrapper flex min-h-svh w-full overflow-x-clip has-[[data-variant=inset]]:bg-sidebar',
              className,
            )}
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                ...style,
              } as React.CSSProperties
            }
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  },
);
SidebarProvider.displayName = 'SidebarProvider';

export const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
  }
>(
  (
    {
      side = 'left',
      variant = 'sidebar',
      collapsible = 'offcanvas',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === 'none') {
      return (
        <div
          ref={ref}
          className={cn(
            'flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile && collapsible !== 'icon') {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile}>
          <SheetContent
            className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
            data-mobile="true"
            data-sidebar="sidebar"
            side={side}
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
          >
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        ref={ref}
        className="group peer text-sidebar-foreground"
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-side={side}
        data-state={state}
        data-variant={variant}
      >
        <div
          className={cn(
            'relative h-svh w-[--sidebar-width] bg-transparent transition-[width] duration-200 ease-linear',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating' || variant === 'inset'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.sm1))]'
              : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]',
          )}
        />
        <div
          className={cn(
            'fixed inset-y-0 z-navbar flex h-svh w-[--sidebar-width] transition-[left,right,width] duration-200 ease-linear',
            side === 'left'
              ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
              : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
            variant === 'floating' || variant === 'inset'
              ? 'p-xxs2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.sm1)_+2px)]'
              : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l border-sidebar-border',
            className,
          )}
          {...props}
        >
          <div
            className={cn(
              'flex h-full w-full flex-col bg-sidebar',
              'group-data-[variant=floating]:rounded-medium group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-overlay',
            )}
            data-sidebar="sidebar"
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);
Sidebar.displayName = 'Sidebar';

export const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);

export const SidebarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={ref}
          className={cn(
            'inline-flex h-[28px] w-[28px] items-center justify-center rounded-xSmall text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            className,
          )}
          data-sidebar="trigger"
          onClick={event => {
            onClick?.(event);
            toggleSidebar();
          }}
          {...props}
        >
          <PanelLeft className="h-[16px] w-[16px]" />
          <span className="sr-only">Toggle Sidebar</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="flex items-center gap-xs2">
        Toggle Sidebar
        <Kbd>{isMac ? '⌘B' : 'Ctrl+B'}</Kbd>
      </TooltipContent>
    </Tooltip>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

export const SidebarRail = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'>
>(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      aria-label="Toggle Sidebar"
      className={cn(
        'absolute inset-y-0 z-dropdown hidden w-xs1 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-xxs2 hover:after:bg-sidebar-border group-data-[side=left]:-right-xs1 group-data-[side=right]:left-0 sm:flex',
        '[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-xxs2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-xxs2',
        className,
      )}
      data-sidebar="rail"
      tabIndex={-1}
      title="Toggle Sidebar"
      onClick={toggleSidebar}
      {...props}
    />
  );
});
SidebarRail.displayName = 'SidebarRail';

export const SidebarInset = React.forwardRef<
  HTMLElement,
  React.ComponentProps<'main'>
>(({ className, ...props }, ref) => {
  return (
    <main
      ref={ref}
      className={cn(
        'relative flex min-h-svh flex-1 flex-col bg-background',
        'peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.sm1)*2)] sm:peer-data-[variant=inset]:m-xxs2 sm:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-xxs2 sm:peer-data-[variant=inset]:ml-0 sm:peer-data-[variant=inset]:rounded-medium sm:peer-data-[variant=inset]:shadow-overlay',
        className,
      )}
      {...props}
    />
  );
});
SidebarInset.displayName = 'SidebarInset';

export const SidebarInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      className={cn(
        'h-[32px] w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        className,
      )}
      data-sidebar="input"
      {...props}
    />
  );
});
SidebarInput.displayName = 'SidebarInput';

export const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col gap-xxs2 p-xs2', className)}
      data-sidebar="header"
      {...props}
    />
  );
});
SidebarHeader.displayName = 'SidebarHeader';

export const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col gap-xxs2 p-xs2', className)}
      data-sidebar="footer"
      {...props}
    />
  );
});
SidebarFooter.displayName = 'SidebarFooter';

export const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      className={cn('mx-xs2 w-auto bg-sidebar-border', className)}
      data-sidebar="separator"
      {...props}
    />
  );
});
SidebarSeparator.displayName = 'SidebarSeparator';

const SCROLL_INDICATOR_MAX = 40;
const SCROLL_THRESHOLD = 1; // Threshold in pixels to account for sub-pixel rendering

function useScrollOverflow(scrollRef: React.RefObject<HTMLDivElement | null>) {
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollBottom, setScrollBottom] = useState(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    let rafId: number | null = null;

    const update = () => {
      const scrollTopValue = el.scrollTop;
      const scrollBottomValue =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      // Apply threshold to avoid showing indicators for sub-pixel differences
      setScrollTop(
        scrollTopValue > SCROLL_THRESHOLD
          ? Math.min(SCROLL_INDICATOR_MAX, scrollTopValue)
          : 0,
      );
      setScrollBottom(
        scrollBottomValue > SCROLL_THRESHOLD
          ? Math.min(SCROLL_INDICATOR_MAX, scrollBottomValue)
          : 0,
      );
    };

    const handleScroll = () => {
      if (rafId) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        update();
        rafId = null;
      });
    };

    update();

    const observer = new MutationObserver(handleScroll);
    observer.observe(el, { childList: true, subtree: true });
    el.addEventListener('scroll', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [scrollRef]);

  return { scrollTop, scrollBottom };
}

function SidebarScrollIndicator({
  position,
  visible,
  scrollRef,
}: {
  position: 'top' | 'bottom';
  visible: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const isTop = position === 'top';
  const Icon = isTop ? ChevronUp : ChevronDown;

  const handleClick = () => {
    scrollRef.current?.scrollTo({
      top: isTop ? 0 : scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div
      role="button"
      tabIndex={-1}
      onClick={handleClick}
      className={cn(
        'absolute left-0 right-0 z-10 flex cursor-pointer items-center justify-center transition-opacity duration-200',
        isTop ? 'top-0' : 'bottom-0',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      style={{ height: SCROLL_INDICATOR_MAX }}
    >
      <div
        className={cn(
          'absolute inset-0',
          isTop
            ? 'bg-gradient-to-b from-sidebar from-50% to-transparent'
            : 'bg-gradient-to-t from-sidebar from-50% to-transparent',
        )}
      />
      <Icon
        className={cn(
          'relative size-[16px] text-sidebar-foreground',
          isTop ? 'mt-0' : 'mt-auto',
        )}
      />
    </div>
  );
}

export const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { scrollTop, scrollBottom } = useScrollOverflow(scrollRef);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col mb-sm2">
      <div
        ref={setRefs}
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-xxs2 overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden group-data-[collapsible=icon]:overflow-x-hidden group-data-[collapsible=icon]:overflow-y-auto',
          className,
        )}
        data-sidebar="content"
        {...props}
      />
      <SidebarScrollIndicator
        position="top"
        visible={scrollTop > 0}
        scrollRef={scrollRef}
      />
      <SidebarScrollIndicator
        position="bottom"
        visible={scrollBottom > 0}
        scrollRef={scrollRef}
      />
    </div>
  );
});
SidebarContent.displayName = 'SidebarContent';

export const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('relative flex w-full min-w-0 flex-col p-xs2', className)}
      data-sidebar="group"
      {...props}
    />
  );
});
SidebarGroup.displayName = 'SidebarGroup';

export const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      className={cn(
        'flex h-[32px] shrink-0 items-center rounded-small px-xs2 text-header-small font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2',
        'group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      data-sidebar="group-label"
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

export const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('w-full text-body-regular', className)}
    data-sidebar="group-content"
    {...props}
  />
));
SidebarGroupContent.displayName = 'SidebarGroupContent';

export const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex w-full min-w-0 flex-col gap-xxs1', className)}
    data-sidebar="menu"
    {...props}
  />
));
SidebarMenu.displayName = 'SidebarMenu';

export const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('group/menu-item relative', className)}
    data-sidebar="menu-item"
    {...props}
  />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-xs2 overflow-hidden rounded-xSmall px-xs3 py-xs2 text-left text-body-regular outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground [&>span:last-child]:truncate [&_svg]:size-[16px] [&_svg]:shrink-0 group-data-[collapsible=icon]:[&_svg]:size-[20px] group-data-[collapsible=icon]:size-[40px] group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0',
  {
    variants: {
      variant: {
        default: '',
        outline:
          'bg-background shadow-[0_0_0_1px_var(--sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_var(--sidebar-accent)]',
      },
      size: {
        small: 'h-[28px] text-body-regular',
        medium: 'h-[32px] text-body-regular',
        large: 'h-[48px] text-body-regular',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
    },
  },
);

export const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'medium',
      tooltip,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        ref={ref}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        data-active={isActive}
        data-sidebar="menu-button"
        data-size={size}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === 'string') {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          align="center"
          hidden={state !== 'collapsed' || isMobile}
          side="right"
          {...tooltip}
        />
      </Tooltip>
    );
  },
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

export const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'pointer-events-none absolute right-xxs1 flex h-[20px] min-w-[20px] select-none items-center justify-center rounded-full px-xxs1 text-body-regular font-medium tabular-nums text-sidebar-foreground peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
      'peer-data-[size=small]/menu-button:top-xxs1',
      'peer-data-[size=medium]/menu-button:top-[5px]',
      'peer-data-[size=large]/menu-button:top-[11px]',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    data-sidebar="menu-badge"
    {...props}
  />
));
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

export const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  const width = useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        'flex h-[32px] items-center gap-xs2 rounded-small px-xs2',
        className,
      )}
      data-sidebar="menu-skeleton"
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-[16px] rounded-xSmall"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-[16px] max-w-[--skeleton-width] flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

export const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'mx-[14px] flex min-w-0 translate-x-px flex-col gap-xxs1 border-l border-sidebar-border px-[10px] py-xxs2',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    data-sidebar="menu-sub"
    {...props}
  />
));
SidebarMenuSub.displayName = 'SidebarMenuSub';

export const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

export const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<'a'> & {
    asChild?: boolean;
    size?: 'small' | 'medium';
    isActive?: boolean;
  }
>(
  (
    { asChild = false, size = 'medium', isActive, className, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'a';

    return (
      <Comp
        ref={ref}
        className={cn(
          'flex h-[28px] min-w-0 -translate-x-px items-center gap-xs2 overflow-hidden rounded-xSmall px-xs2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:size-[16px] [&_svg]:shrink-0 [&_svg]:text-sidebar-accent-foreground',
          'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
          className,
        )}
        data-active={isActive}
        data-sidebar="menu-sub-button"
        data-size={size}
        {...props}
      />
    );
  },
);
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';
